export type Table = {
  id: number;
  name?: string;
  locked?: boolean;
  owner_id?: number;
  seats_taken: number;
  number_of_seats: number;
  click?: "create";
  room_id: number;
};

export type RoomState = {
  currentTable: any;
  loading: boolean;
  room?: Room;
  tables: Table[];
  error?: string | null;
  tutorialSeen: boolean;
};

export type Room = {
  id: string;
  slug: string;
  name: string;
  room_type: "vip" | "normal";
  min_user_level: number;
};

export type TableItem = {
  id: number;
  legacyRoomId?: number;
  name: string | null;
  room_id: number;
  number_of_seats: number;
  seats_taken: number;
  locked: boolean;
  owner_id: number | null;
  seats: Array<any>;
  minimal_chips: number;
};

export type UserProfile = {
  id: string;
  numericId: number;
  username: string;
  email: string;
  isSoundEffectsEnabled: boolean;
  avatarFileId: string | null;
  chips: number;
  isVip: boolean;
  level: number;
  user_purchases: { [key: string]: boolean };
  isEmailEnabled: boolean;
  isMobileEnabled: boolean;
  isHandPowerEnabled: boolean;
  collectedAt: string;
  bonusHours: string;
  vipExpiredAt?: string | null;
  accessToken?: string;
  refreshToken?: string;
};

export type LobbyRoom = {
  id: string;
  name: string;
  minLevel: number;
  entryFee: number;
  isVip: boolean;
  createdAt: string;
  activeTablesCount: number;
};

export type Package = {
  id: string;
  chipAmount?: number;
  chips?: number; // Alias for chipAmount (for backward compatibility)
  price: number;
  name: string;
  logoId?: string;
  description?: string;
  type: "CHIPS" | "VIP_MEMBERSHIP" | "OTHER_TYPE";
  vipDurationDays?: number;
  currencyId?: string;
  currency: Array<any>;
  displayOrder?: number;
  isActive?: boolean;
  isPromoted?: boolean;
  metadata?: {
    applePay?: {
      id: number; // Your internal Apple Pay ID
      productId: string; // App Store product ID (e.g., "someId_1")
    };
    googlePay?: {
      id: number; // Your internal Google Pay ID
      productId: string; // Play Store product ID (e.g., "someId_1")
    };
    [key: string]: any; // Allow other metadata fields
  };
  createdAt?: string;
  updatedAt?: string;
};

export type UserStatistics = {
  player: {
    avatar?: any;
    username: string;
    registrationDate: string;
    lastLoginDate?: any;
    vip_expired_at: string | null;
  };
  level: {
    current: number;
    name: string;
  };
  gamesData: {
    svarka_wins: number;
    svarka_loses: number;
    svarka_chips_wins: string;
    svarka_time_played: number;
    svarka_last_time_played?: any;
    svarka_level: number;
    svarka_minimal_experience: number;
    svarka_maximal_experience: number;
  };
  statistics: {
    svarka_time_played: string;
    winLoseRatio: number;
    totalChipsGained: any;
  };
};

export type UpdateProfilePayload = {
  username?: string;
  avatarFileId?: string | null;
  isSoundEffectsEnabled?: boolean;
  isMobileNotificationsEnabled?: boolean;
  isEmailNotificationsEnabled?: boolean;
  isHandPowerMeterEnabled?: boolean;

  remarks?: string;
};

export type Bonus = {
  bonusHours: number;
  bonusAmounts: number[];
  vipAmounts: number[];
  chips: number;
  collectedAt: string;
  followingDaysCollected: number;
};

export type BonusState = {
  timer: string;

  bonusReady: boolean;

  bonus?: Bonus;

  daily: Array<{
    day: number;
    chips: number;
    vipChips: number;
    collected: boolean;
    current: boolean;
  }>;

  loading: boolean;
  error?: string;
};

export type NotificationItem = {
  id: string;
  title: string;
  body?: string;
  createdAt: string;
  read?: boolean;
  level: string;
  type: string;
};

export type RankingEntry = {
  position?: number;
  username?: string;
  chips?: number;
  [k: string]: any;
};

export type RankingState = {
  loading: boolean;
  error?: string;
  items: RankingEntry[];
  total: number;
};

export type SnGTournament = {
  id: number;
  entry_level: number;
  entry_size: number;
  users: Array<string | number>;
  active_tables: number;
  registered_players: number;
  registered?: boolean;
};

export type TournamentsState = {
  loading: boolean;
  error?: string;
  items: SnGTournament[];
  byEntryLevel: Record<number, SnGTournament>;
  subscribed: boolean;
};

export type MyTable = {
  id: string | number;
  name: string;
  chips: number;
  room_id: string | number;
};

export type CreateSpecialTablePayload = {
  stake: number;
  minimalAmount: number;
  maximalAmount: number;
  maxSeats: number;
  turnDuration: number;
  isPrivate: boolean;
};

export interface PlayerReportRequestPayload {
  reportedId: string;
  reportType: ReportType;
  reason?: string;
  gameContext?: string;
}

export enum ReportType {
  INAPPROPRIATE_USERNAME = "INAPPROPRIATE_USERNAME",
  INAPPROPRIATE_AVATAR = "INAPPROPRIATE_AVATAR",
  HARASSMENT = "HARASSMENT",
  OFFENSIVE_BEHAVIOR = "OFFENSIVE_BEHAVIOR",
  SPAM = "SPAM",
  OTHER = "OTHER",
}
